import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { fetchGraphql } from '@zoopla/fetch-graphql';
import { setupLogger } from '@zoopla/logger';
import {
    FetchLandingPageDocument,
    FetchLandingPageQuery,
    FetchLandingPageQueryVariables,
} from './queries/landingPage.generated';
import { Props } from './landing-pages.types';
import { Slug } from '@zoopla/landing-pages/types';
import { ZooplaGqlError } from '@zoopla/shared/zoopla-graphql-error';

const logger = setupLogger({
    page: 'landingPage',
    custodian: 'platform',
});

const getStaticPropsFactory =
    (slug: Slug) =>
    async (
        ctx: GetStaticPropsContext
    ): Promise<GetStaticPropsResult<Props | Record<string, unknown>>> => {
        const { preview } = ctx;

        try {
            const { data, errors, extensions } = await fetchGraphql<
                FetchLandingPageQuery,
                FetchLandingPageQueryVariables
            >(FetchLandingPageDocument, {
                slug,
                preview,
                articleLimit: 12,
            });

            if (errors) {
                throw new ZooplaGqlError(errors, extensions);
            }

            if (
                data?.landingPage?.__typename === 'CMSError' ||
                data?.landingPage?.__typename !== 'LandingPage'
            ) {
                return {
                    notFound: true,
                    revalidate: true,
                };
            }

            if (data?.landingPage?.__typename === 'LandingPage') {
                const {
                    hero,
                    seoInformation: {
                        metaDescription,
                        openGraphImage,
                        pageTitle,
                        index,
                        follow,
                    },
                    slug: pageSlug,
                    button,
                    contentBlock,
                    subNavigation,
                } = data.landingPage;

                return {
                    revalidate: 300,
                    props: {
                        button: button || '',
                        contentBlock: contentBlock || [],
                        hero: {
                            title: hero.title,
                            description: hero.description,
                            image: { url: hero.image.url },
                        },
                        seoInformation: {
                            metaDescription: metaDescription || '',
                            openGraphImage: openGraphImage || '',
                            pageTitle: pageTitle || '',
                            index: index,
                            follow: follow,
                        },
                        slug: pageSlug,
                        subNavigation,
                    },
                };
            }

            return {
                props: {},
            };
        } catch (err) {
            logger.error(
                { pageSlug: slug, err },
                'Error querying landing page'
            );

            throw new Error(
                `Error querying landing page: ${JSON.stringify(err)}`
            );
        }
    };

export default getStaticPropsFactory;
